// Check if the class has already been defined to prevent re-declaration
if (!customElements.get('scrolling-review-strip')) {
  class ScrollingReviewStrip extends HTMLElement {
    constructor() {
      super();
      console.log('ScrollingReviewStrip constructor initialized');
      
      this.track = this.querySelector('.scrolling-review-strip__track');
      this.loadingElement = this.querySelector('.scrolling-review-strip__loading');
      this.productId = this.dataset.productId || '';
      this.scrollSpeed = parseInt(this.dataset.scrollSpeed, 10) || 5; // Speed: 1 (slowest) to 10 (fastest)
      this.locale = this.dataset.locale || '';
      this.isPaused = false;
      this.currentOffset = 0;
      this.scrollStep = 0;
      this.loopPoint = 0;
      this.animationFrameId = null;
      this.originalReviewElements = []; // Store original elements

      console.log('Product ID from data attribute (scrolling):', this.productId);
      console.log('Scroll speed setting:', this.scrollSpeed);
      console.log('Locale from data attribute:', this.locale);
      
      // Skip initialization if product ID is missing
      if (!this.productId) {
        console.warn('Missing data-product-id in scrolling-review-strip. Skipping initialization.');
        return;
      }
      
      // Only initialize if we have a track element
      if (this.track) {
        // Initialize after DOM is ready to ensure product-reviews.js has loaded reviews
        if (document.readyState === 'complete') {
          this.init();
        } else {
          window.addEventListener('load', () => this.init());
        }
      } else {
        console.warn('Track element not found, skipping initialization');
      }
    }
    
    async init() {
      console.log('Initializing scrolling review strip component');
      try {
        // Find reviews from the product-reviews component
        const reviews = this.findReviewsFromProductReviewsComponent();
        console.log('Checking for reviews from product-reviews component...');
        console.log('Result from findReviewsFromProductReviewsComponent:', reviews ? `${reviews.length} reviews found` : 'null');

        // If product-reviews component exists and has loaded reviews, use them
        if (reviews && reviews.length > 0) {
          console.log(`Found ${reviews.length} reviews from product-reviews component`);
          window.loadedReviews = reviews;
          this.renderReviews();
          this.initScrolling();
          console.log('Scrolling review strip component initialized successfully');
        } else {
          // Otherwise, attempt to load reviews from CSV using the productId
          console.log('No reviews found from product-reviews component, trying to load from CSV directly');
          await this.loadReviewsFromCSV();
        }
      } catch (error) {
        console.error('Error initializing scrolling review strip:', error);
        this.showEmptyState();
      }
    }
    
    findReviewsFromProductReviewsComponent() {
      // Try to find the product-reviews component
      const productReviewsElement = document.querySelector('product-reviews');
      if (productReviewsElement && productReviewsElement._initialized) {
        console.log('Found initialized product-reviews component');
        
        // Access the allReviews property from the component instance
        if (productReviewsElement.allReviews && productReviewsElement.allReviews.length > 0) {
          // If we have a product ID, filter reviews to match this product
          if (this.productId) {
            console.log(`Filtering reviews for product ID: ${this.productId}`);
            const filteredReviews = productReviewsElement.allReviews.filter(review => {
              // Check various possible product ID fields
              // Convert to string for comparison to handle numeric vs string IDs
              return (
                (review.product_id && review.product_id.toString() === this.productId.toString()) ||
                (review.productId && review.productId.toString() === this.productId.toString())
              );
            });
            
            if (filteredReviews.length > 0) {
              console.log(`Found ${filteredReviews.length} reviews for product ID ${this.productId}`);
              return filteredReviews;
            } else {
              console.log(`No reviews found for product ID ${this.productId}`);
              return null;
            }
          }
          
          // If no product ID specified, return all reviews
          return productReviewsElement.allReviews;
        }
      }
      return null;
    }
    
    async loadReviewsFromCSV() {
      console.log('Loading reviews from CSV directly');
      
      // Ensure we have a track element before proceeding
      if (!this.track) {
        console.error('Track element not found, cannot load reviews');
        return;
      }
      
      // Double-check that we have a product ID
      if (!this.productId) {
        console.error('Cannot load reviews: Missing product ID');
        this.showEmptyState();
        return;
      }
      
      // Get the current locale from the data attribute or fallback to document
      const currentLocale = (this.locale || document.documentElement.lang || 'en').toUpperCase();
      const localeKey = (this.locale || document.documentElement.lang || 'en').toLowerCase();
      console.log('Current locale for reviews:', currentLocale, 'locale key:', localeKey);
      
      // Try to get URLs from the JSON data in the page (same as product-reviews.js)
      const urlsElement = document.getElementById('review-csv-urls') || document.getElementById('product-reviews-urls');
      let urls = {};
      
      if (urlsElement) {
        try {
          urls = JSON.parse(urlsElement.textContent);
          console.log('Found review URLs in JSON element:', urls);
        } catch (error) {
          console.error('Error parsing review URLs:', error);
        }
      } else {
        console.warn('No review-csv-urls or product-reviews-urls element found');
      }
      
      // Log all available URLs for debugging
      console.log('Available URLs from JSON:');
      for (const [key, url] of Object.entries(urls)) {
        console.log(`${key}: ${url}`);
      }
      
      // First try to use the URL from the JSON if available
      if (this.productId && urls[localeKey]) {
        try {
          // Verify that the URL contains the product ID to avoid loading empty ID files
          const urlString = urls[localeKey].toString();
          const containsProductId = urlString.indexOf(`_${this.productId}.csv`) > -1;
          
          if (!containsProductId) {
            console.warn(`URL does not contain product ID ${this.productId}, skipping:`, urlString);
            throw new Error('URL does not contain product ID');
          }
          
          console.log('Trying to load locale-specific file from JSON:', urls[localeKey]);
          
          const response = await fetch(urls[localeKey]);
          console.log('JSON URL response status:', response.status);
          
          if (response.ok) {
            console.log(`Successfully loaded ${localeKey} reviews from JSON URL`);
            const csvText = await response.text();
            console.log('CSV text first 100 chars:', csvText.substring(0, 100));
            this.parseReviews(csvText);
            this.renderReviews();
            this.initScrolling();
            return;
          } else {
            console.warn(`${localeKey} reviews file from JSON not found:`, response.status);
          }
        } catch (error) {
          console.error(`Error loading ${localeKey} reviews from JSON:`, error);
        }
      }
      
      // If JSON URL fails or isn't available, fall back to direct file path
      let csvUrl;
      
      if (this.productId) {
        // If we have a product ID, use it to construct the CSV URL
        csvUrl = `/assets/${currentLocale}_${this.productId}.csv`;
        console.log('Falling back to direct file path:', csvUrl);
      } else {
        // If no product ID is provided, try to load a default CSV
        csvUrl = `/assets/${currentLocale}_default.csv`;
        console.log('No product ID provided, attempting to load default CSV from:', csvUrl);
      }
      
      try {
        console.log('Final CSV URL to fetch:', csvUrl);
        const response = await fetch(csvUrl);
        if (response.ok) {
          const csvText = await response.text();
          console.log('Successfully loaded CSV, parsing reviews');
          this.parseReviews(csvText);
          this.renderReviews();
          this.initScrolling();
        } else {
          // If locale-specific file fails and we're not using English, try English fallback
          if (currentLocale !== 'EN') {
            // First try English from JSON
            if (urls['en']) {
              try {
                // Verify that the URL contains the product ID
                const urlString = urls['en'].toString();
                const containsProductId = urlString.indexOf(`_${this.productId}.csv`) > -1;
                
                if (!containsProductId) {
                  console.warn(`English URL does not contain product ID ${this.productId}, skipping:`, urlString);
                  throw new Error('URL does not contain product ID');
                }
                
                console.log('Falling back to English file from JSON:', urls['en']);
                
                const response = await fetch(urls['en']);
                console.log('English JSON fallback response status:', response.status);
                
                if (response.ok) {
                  console.log('Successfully loaded English fallback reviews from JSON');
                  const csvText = await response.text();
                  console.log('English CSV text first 100 chars:', csvText.substring(0, 100));
                  this.parseReviews(csvText);
                  this.renderReviews();
                  this.initScrolling();
                  return;
                } else {
                  console.warn('English JSON fallback reviews file not found:', response.status);
                }
              } catch (error) {
                console.error('Error loading English JSON fallback reviews:', error);
              }
            }
            
            // If JSON English fallback fails, try direct English file
            const fallbackUrl = this.productId
              ? `/assets/EN_${this.productId}.csv`
              : `/assets/EN_default.csv`;
            
            console.log('Trying direct English fallback file:', fallbackUrl);
            const fallbackResponse = await fetch(fallbackUrl);
            
            if (fallbackResponse.ok) {
              const fallbackCsvText = await fallbackResponse.text();
              console.log('Successfully loaded direct English fallback CSV');
              this.parseReviews(fallbackCsvText);
              this.renderReviews();
              this.initScrolling();
              return;
            }
          }
          
          // Try the legacy file as a last resort
          if (urls['legacy'] && this.productId) {
            try {
              console.log('Trying legacy file as last resort:', urls['legacy']);
              
              const response = await fetch(urls['legacy']);
              console.log('Legacy file response status:', response.status);
              
              if (response.ok) {
                console.log('Successfully loaded legacy reviews file');
                const csvText = await response.text();
                console.log('Legacy CSV text first 100 chars:', csvText.substring(0, 100));
                this.parseReviews(csvText);
                this.renderReviews();
                this.initScrolling();
                return;
              } else {
                console.warn('Legacy reviews file not found:', response.status);
              }
            } catch (error) {
              console.error('Error loading legacy reviews:', error);
            }
          }
          
          console.error('Failed to load CSV:', response.status);
          this.showEmptyState();
        }
      } catch (error) {
        console.error('Error loading CSV:', error);
        this.showEmptyState();
      }
    }
    
    parseReviews(csvText) {
      console.log('Parsing CSV reviews');
      
      // Split the CSV into lines
      const lines = csvText.split('\n');
      console.log(`CSV has ${lines.length} lines`);
      
      if (lines.length === 0) {
        console.error('CSV file is empty');
        return;
      }
      
      // Get the header line and parse column names
      const headers = this.parseCSVLine(lines[0]);
      console.log('CSV headers:', headers);
      
      // Parse each review line
      const reviews = [];
      
      // Process each line
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        try {
          const values = this.parseCSVLine(lines[i]);
          
          // Create review object from headers and values
          const review = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';
            if (typeof value === 'string') {
              value = value.trim();
            }
            review[header] = value;
          });
          
          // Only add published reviews
          if (review.state && (review.state.toLowerCase() === 'published' || review.state.toLowerCase() === 'unpublished')) {
            reviews.push(review);
          }
        } catch (error) {
          console.error(`Error parsing review line ${i}:`, error);
        }
      }
      
      console.log(`Successfully parsed ${reviews.length} reviews`);
console.log('Updating window.loadedReviews with parsed CSV data.');
      window.loadedReviews = reviews;
    }
    // Removed erroneous log statement here
    parseCSVLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          // Check if this is an escaped quote (""") inside a quoted field
          if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++; // Skip the next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          // End of field
          result.push(current.replace(/^"|"$/g, '').replace(/""/g, '"'));
          current = '';
        } else {
          current += char;
        }
      }
      
      // Add the last field
      result.push(current.replace(/^"|"$/g, '').replace(/""/g, '"'));
      
      return result;
    }
    
    renderReviews() {
      console.log('Rendering scrolling reviews');
      
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      
      // Get reviews from the global array
      let reviews = [];
      
      if (window.loadedReviews && Array.isArray(window.loadedReviews)) {
        // First filter by product ID if specified
        let filteredByProductId = [];
        if (this.productId) {
          filteredByProductId = window.loadedReviews.filter(review => {
            // Check various possible product ID fields
            // Convert to string for comparison to handle numeric vs string IDs
            return (
              (review.product_id && review.product_id.toString() === this.productId.toString()) ||
              (review.productId && review.productId.toString() === this.productId.toString()) ||
              // Some CSV files might not have product_id column, so use all reviews
              (!review.product_id && !review.productId)
            );
          });
          console.log(`Filtered ${filteredByProductId.length} reviews for product ID ${this.productId}`);
        } else {
          filteredByProductId = window.loadedReviews;
          console.log(`Using all ${filteredByProductId.length} available reviews`);
        }
        
        // Then filter to only include reviews with images
        reviews = filteredByProductId.filter(review => {
          return review.imageUrl && review.imageUrl.trim() !== '';
        });
        console.log(`Found ${reviews.length} reviews with images`);
      }
      
      if (reviews.length === 0) {
        console.log('No reviews to display');
        this.showEmptyState();
        return;
      }
      
      // Create review elements
      const reviewElements = reviews.map(review => this.createReviewElement(review));
      
      // Clear loading indicator and append reviews
      this.track.innerHTML = '';
      
      // Add reviews to the track
      reviewElements.forEach(element => {
        this.track.appendChild(element);
      });
      
      // Store original elements for reference
      this.originalReviewElements = [...reviewElements];
      
      // We'll clone elements dynamically in initScrolling() after measuring widths
      // This ensures we have enough content for smooth scrolling
    }
    
    createReviewElement(review) {
      console.log('Creating review element for:', review.title);
      const reviewElement = document.createElement('div');
      reviewElement.classList.add('scrolling-review-strip__item');
      
      // Parse image URLs (they might be comma-separated)
      let imageUrl = '';
      if (review.imageUrl) {
        // Only use the first image
        imageUrl = review.imageUrl.split(',')[0].trim();
      }
      
      // Extract the actual title from the full title field
      let cleanTitle = '';
      if (review.title) {
        // Split by newlines and filter out empty lines
        const titleParts = review.title.split('\n').filter(part => part.trim());
        
        // The last non-empty part is usually the actual title
        if (titleParts.length > 0) {
          // Get the last part and trim it
          cleanTitle = titleParts[titleParts.length - 1].trim();
          
          // If it starts with spaces or tabs, clean those up too
          cleanTitle = cleanTitle.replace(/^\s+/, '');
          
          console.log('Extracted clean title:', cleanTitle);
        } else {
          // If we couldn't parse it properly, just use the original title
          cleanTitle = review.title;
        }
      }
      
      // Create review HTML
      reviewElement.innerHTML = `
        ${imageUrl ? `<img src="${imageUrl}" alt="Review image" class="scrolling-review-strip__image">` : ''}
        <div class="scrolling-review-strip__rating">
          ${this.createStarRating(parseFloat(review.rating))}
        </div>
        ${cleanTitle ? `<h4 class="scrolling-review-strip__title">${cleanTitle}</h4>` : ''}
        <div class="scrolling-review-strip__author">— ${review.author || 'Anonymous'}</div>
      `;
      
      return reviewElement;
    }
    
    createStarRating(rating) {
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating % 1 >= 0.5;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
      
      return `
        <div class="scrolling-review-strip__stars" role="img" aria-label="${rating} out of 5 stars">
          ${Array(fullStars).fill('<span class="scrolling-review-strip__star">★</span>').join('')}
          ${hasHalfStar ? '<span class="scrolling-review-strip__star">★</span>' : ''}
          ${Array(emptyStars).fill('<span class="scrolling-review-strip__star scrolling-review-strip__star--empty">☆</span>').join('')}
        </div>
      `;
    }
    
    initScrolling() {
      console.log('Initializing JavaScript scroll animation');
      this.isPaused = false; // Ensure not paused initially
      this.currentOffset = 0; // Reset offset
      this.track.style.transform = 'translate3d(0px, 0, 0)'; // Reset initial position

      const container = this.querySelector('.scrolling-review-strip__container');
      if (!container || !this.track || this.originalReviewElements.length === 0) {
        console.error('Container, track, or original elements not found for scrolling init.');
        return;
      }

      // Wait for images, ensure width, then start loop
      this.waitForImagesToLoad().then(() => {
        this.ensureSufficientTrackWidth(container);

        // Calculate the point at which the scroll should loop (width of the original set)
        this.loopPoint = this.originalReviewElements.reduce((width, el) => {
          const style = window.getComputedStyle(el);
          const marginRight = parseFloat(style.marginRight) || 0;
          return width + el.offsetWidth + marginRight;
        }, 0);

        if (this.loopPoint <= 0) {
          console.error('Loop point calculation failed (<= 0). Cannot start animation.');
          return;
        }
        console.log(`Calculated loop point: ${this.loopPoint}px`);

        // Calculate scroll step based on speed setting (1-10)
        // Map speed 1 to ~0.5px/frame, speed 10 to ~2px/frame
        const minPxPerFrame = 0.5;
        const maxPxPerFrame = 2.0;
        this.scrollStep = minPxPerFrame + (this.scrollSpeed - 1) * ((maxPxPerFrame - minPxPerFrame) / 9);
        console.log(`Calculated scroll step: ${this.scrollStep.toFixed(2)}px/frame for speed ${this.scrollSpeed}`);

        // Add interaction listeners (hover/touch)
        this.addInteractionListeners();

        // Start the animation loop
        this.startScrollLoop();
      });
    }

    startScrollLoop() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId); // Cancel previous loop if any
      }
      console.log('Starting scroll loop');
      this.scrollLoop(); // Start the loop
    }

    scrollLoop() {
      if (this.isPaused) {
        // If paused, stop requesting new frames but keep the ID to know it's paused
        console.log('Scroll loop paused');
        return;
      }

      this.currentOffset -= this.scrollStep;

      // Check if we've scrolled past the loop point
      if (this.currentOffset <= -this.loopPoint) {
        // Reset the offset seamlessly
        const overlap = Math.abs(this.currentOffset) - this.loopPoint;
        this.currentOffset = -overlap; // Start the next loop slightly offset
        console.log(`Loop point reached. Resetting offset to ${this.currentOffset.toFixed(2)}px`);
      }

      // Apply the transform
      this.track.style.transform = `translate3d(${this.currentOffset}px, 0, 0)`;

      // Request the next frame
      this.animationFrameId = requestAnimationFrame(this.scrollLoop.bind(this));
    }
    
    /**
     * Wait for all images in the track to load
     * @returns {Promise} Resolves when all images are loaded
     */
    waitForImagesToLoad() {
      console.log('Waiting for images to load...');
      const images = this.track.querySelectorAll('img');
      
      if (images.length === 0) {
        console.log('No images to wait for');
        return Promise.resolve();
      }
      
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) {
          return Promise.resolve();
        } else {
          return new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to prevent hanging
          });
        }
      });
      
      return Promise.all(imagePromises).then(() => {
        console.log(`All ${images.length} images loaded`);
      });
    }
    
    /**
     * Ensure the track is at least 2x the container width by cloning elements as needed
     * @param {HTMLElement} container - The container element
     */
    ensureSufficientTrackWidth(container) {
      const containerWidth = container.offsetWidth;
      let trackWidth = this.track.scrollWidth;
      const minRequiredWidth = containerWidth * 2;
      
      console.log(`Container width: ${containerWidth}px, Current track width: ${trackWidth}px`);
      console.log(`Minimum required width: ${minRequiredWidth}px`);
      
      // If we don't have enough original reviews, clone them until we have enough
      if (this.originalReviewElements.length === 0) {
        console.warn('No original review elements found');
        return;
      }
      
      // Keep cloning until we have at least 2x container width
      let cloneCount = 0;
      while (trackWidth < minRequiredWidth) {
        // Clone all original elements
        this.originalReviewElements.forEach(element => {
          const clone = element.cloneNode(true);
          this.track.appendChild(clone);
          cloneCount++;
        });
        
        // Update track width after adding clones
        trackWidth = this.track.scrollWidth;
        
        // Safety check to prevent infinite loop
        if (cloneCount > 100) {
          console.warn('Safety limit reached when cloning elements');
          break;
        }
      }
      
      console.log(`Added ${cloneCount} cloned elements to reach sufficient width`);
      console.log(`Final track width: ${this.track.scrollWidth}px`);
    }
    
    addInteractionListeners() {
        // --- Hover Pause (Desktop) ---
        this.track.addEventListener('mouseenter', () => {
            if (!this.isPaused) {
                console.log('Hover: Pausing scroll');
                this.isPaused = true;
                // No need to cancel frame here, scrollLoop checks isPaused
            }
        });

        this.track.addEventListener('mouseleave', () => {
            if (this.isPaused) {
                console.log('Hover: Resuming scroll');
                this.isPaused = false;
                this.scrollLoop(); // Restart the loop if it was paused
            }
        });

        // --- Touch Pause (Mobile) ---
        this.track.addEventListener('touchstart', () => {
            if (!this.isPaused) {
                console.log('Touch: Pausing scroll');
                this.isPaused = true;
            }
        }, { passive: true }); // Use passive for better scroll performance

        this.track.addEventListener('touchend', () => {
            if (this.isPaused) {
                console.log('Touch: Resuming scroll');
                this.isPaused = false;
                this.scrollLoop(); // Restart the loop
            }
        });

        this.track.addEventListener('touchcancel', () => {
            if (this.isPaused) {
                console.log('Touch Cancel: Resuming scroll');
                this.isPaused = false;
                this.scrollLoop(); // Restart the loop
            }
        });
    }

    // Cleanup animation frame on disconnect
    disconnectedCallback() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
        console.log('ScrollingReviewStrip disconnected, animation frame cancelled.');
      }
    }
    
    showEmptyState() {
      console.log('Showing empty state');
      
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      
      // Check if track exists before trying to modify it
      if (this.track) {
        const emptyElement = document.createElement('div');
        emptyElement.classList.add('scrolling-review-strip__empty');
        emptyElement.textContent = 'No reviews available';
        this.track.innerHTML = '';
        this.track.appendChild(emptyElement);
      } else {
        console.warn('Cannot show empty state: track element not found');
      }
    }
  }
  
  // Define the custom element
  customElements.define('scrolling-review-strip', ScrollingReviewStrip);
  console.log('ScrollingReviewStrip custom element defined');
  
  // Removed complex initialization logic (MutationObserver, _initialized flag)
  // Relying on standard custom element definition and constructor/load event.
  
  // Make reviews globally available for other components
  // This is needed because the main product-reviews.js doesn't expose the reviews globally
  if (!window.loadedReviews) {
    window.loadedReviews = [];
    
    // Patch the original ProductReviews class to share reviews
    const originalProductReviews = customElements.get('product-reviews');
    if (originalProductReviews) {
      const originalParseReviews = originalProductReviews.prototype.parseReviews;
      
      originalProductReviews.prototype.parseReviews = function(csvText) {
        // Call the original method
        originalParseReviews.call(this, csvText);
        
        // Share the reviews globally
        window.loadedReviews = this.allReviews;
        console.log('Shared reviews globally:', window.loadedReviews.length);
      };
      
      console.log('Patched ProductReviews.parseReviews to share reviews globally');
    }
  }
  
  // Add a debug function to list all instances in the DOM
  window.debugScrollingReviewStrips = function() {
    const elements = document.querySelectorAll('scrolling-review-strip');
    console.log(`Found ${elements.length} scrolling-review-strip elements in the DOM:`);
    
    elements.forEach((el, index) => {
      console.log(`Element ${index + 1}:`);
      console.log(`  Product ID: "${el.dataset.productId || 'empty'}"`);
      console.log(`  Locale: "${el.dataset.locale || 'empty'}"`);
      console.log(`  Initialized: ${el._initialized ? 'yes' : 'no'}`);
      console.log(`  Has track element: ${el.querySelector('.scrolling-review-strip__track') ? 'yes' : 'no'}`);
    });
    
    // Check for JSON URLs
    const urlsElement = document.getElementById('review-csv-urls');
    if (urlsElement) {
      try {
        const urls = JSON.parse(urlsElement.textContent);
        console.log('Review CSV URLs found in the page:');
        for (const [key, url] of Object.entries(urls)) {
          console.log(`  ${key}: ${url}`);
        }
      } catch (error) {
        console.error('Error parsing review URLs:', error);
      }
    } else {
      console.log('No review-csv-urls element found in the page');
    }
    
    return `Found ${elements.length} scrolling-review-strip elements. See console for details.`;
  };
}